import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Phone, RefreshCw, CheckCircle, XCircle, Clock, Loader2, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AircallSourcePage() {
  const { personId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [person, setPerson] = useState(null);
  const [aircallSource, setAircallSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isConnectOpen, setIsConnectOpen] = useState(false);

  const [connectForm, setConnectForm] = useState({
    aircall_user_id: '',
    phone_numbers: '',
    sync_calls: true,
    sync_from_days: 30
  });
  const [aircallUsers, setAircallUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchPerson();
    fetchAircallSource();
  }, [personId]);

  const fetchPerson = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerson(response.data);
    } catch (error) {
      toast.error('Clone not found');
      navigate('/clones');
    }
  };

  const fetchAircallSource = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/aircall/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAircallSource(response.data);
    } catch (error) {
      // 404 = not connected yet
    } finally {
      setLoading(false);
    }
  };

  const fetchAircallUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/aircall/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAircallUsers(response.data.users || []);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Impossible de recuperer les utilisateurs Aircall';
      toast.error(msg);
      if (msg.includes('credentials not configured')) {
        toast.info('Configurez vos identifiants Aircall dans Settings');
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleConnect = async () => {
    try {
      const phoneArr = connectForm.phone_numbers
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);

      await axios.post(`${API}/persons/${personId}/sources/aircall`, {
        aircall_user_id: connectForm.aircall_user_id || null,
        phone_numbers: phoneArr,
        sync_calls: connectForm.sync_calls,
        sync_from_days: connectForm.sync_from_days
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Source Aircall connectee');
      setIsConnectOpen(false);
      fetchAircallSource();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/persons/${personId}/sources/aircall/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Synchronisation Aircall lancee');
      setTimeout(fetchAircallSource, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer la source Aircall ?')) return;
    try {
      await axios.delete(`${API}/persons/${personId}/sources/aircall`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Source Aircall supprimee');
      setAircallSource(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Aircall">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Aircall">
      <div className="max-w-3xl space-y-6" data-testid="aircall-source-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/clones/${personId}/sources`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-xl font-heading font-bold">
              <Phone className="w-5 h-5 inline mr-2 text-sky-500" />
              Aircall — {person?.name}
            </h2>
            <p className="text-sm text-muted-foreground">Synchronisation des appels via Aircall</p>
          </div>
        </div>

        {/* Status Card */}
        {aircallSource ? (
          <Card data-testid="aircall-status-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading">Source Aircall</CardTitle>
                <div className="flex items-center gap-2">
                  {aircallSource.last_sync_status === 'completed' && <Badge variant="outline" className="text-emerald-600 border-emerald-200"><CheckCircle className="w-3 h-3 mr-1" />Synchro OK</Badge>}
                  {aircallSource.last_sync_status === 'syncing' && <Badge variant="outline" className="text-amber-600 border-amber-200"><Clock className="w-3 h-3 mr-1" />Sync en cours</Badge>}
                  {aircallSource.last_sync_status === 'failed' && <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Erreur</Badge>}
                  {aircallSource.last_sync_status === 'pending' && <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />En attente</Badge>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">User ID Aircall</p>
                  <p className="text-sm font-mono">{aircallSource.aircall_user_id || 'Tous'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Appels synchronises</p>
                  <p className="text-sm font-medium">{aircallSource.calls_synced_count || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Derniere sync</p>
                  <p className="text-sm">{aircallSource.last_sync_at ? new Date(aircallSource.last_sync_at).toLocaleString('fr-FR') : 'Jamais'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Numeros</p>
                  <p className="text-sm font-mono">{aircallSource.phone_numbers?.join(', ') || 'Aucun'}</p>
                </div>
              </div>

              {aircallSource.last_sync_error && (
                <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  {aircallSource.last_sync_error}
                </div>
              )}

              <Separator />

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button onClick={handleSync} disabled={syncing} size="sm" data-testid="sync-aircall-btn">
                    {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Synchroniser
                  </Button>
                </div>
                {isOwner && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={handleDelete} data-testid="delete-aircall-btn">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Supprimer
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="aircall-connect-card">
            <CardContent className="py-12 text-center space-y-4">
              <Phone className="w-12 h-12 mx-auto text-sky-500/40" />
              <div>
                <h3 className="font-heading font-semibold">Connecter Aircall</h3>
                <p className="text-sm text-muted-foreground mt-1">Synchronisez automatiquement les appels depuis Aircall</p>
              </div>
              <Button onClick={() => { setIsConnectOpen(true); fetchAircallUsers(); }} data-testid="connect-aircall-btn">
                Connecter Aircall
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Connect Dialog */}
        <Dialog open={isConnectOpen} onOpenChange={setIsConnectOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Connecter Aircall</DialogTitle>
              <DialogDescription>Selectionnez l'utilisateur Aircall a associer a ce clone</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* User selection */}
              <div className="space-y-2">
                <Label>Utilisateur Aircall</Label>
                {loadingUsers ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement des utilisateurs...
                  </div>
                ) : aircallUsers.length > 0 ? (
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {aircallUsers.map(u => (
                      <button
                        key={u.id}
                        onClick={() => {
                          setConnectForm({
                            ...connectForm,
                            aircall_user_id: u.id,
                            phone_numbers: u.numbers?.map(n => n.digits).join(', ') || ''
                          });
                        }}
                        className={`flex items-center justify-between p-3 rounded-md border text-left transition-colors ${
                          connectForm.aircall_user_id === u.id
                            ? 'border-sky-500 bg-sky-500/5'
                            : 'border-border hover:border-sky-300'
                        }`}
                        data-testid={`aircall-user-${u.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{u.availability}</Badge>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Aucun utilisateur trouve. Verifiez vos identifiants Aircall dans Settings.</p>
                    <Input
                      placeholder="ID utilisateur Aircall (manuel)"
                      value={connectForm.aircall_user_id}
                      onChange={e => setConnectForm({ ...connectForm, aircall_user_id: e.target.value })}
                      data-testid="aircall-user-id-input"
                    />
                  </div>
                )}
              </div>

              {/* Phone numbers */}
              <div className="space-y-2">
                <Label htmlFor="phones">Numeros de telephone</Label>
                <Input
                  id="phones"
                  placeholder="+33612345678, +33698765432"
                  value={connectForm.phone_numbers}
                  onChange={e => setConnectForm({ ...connectForm, phone_numbers: e.target.value })}
                  data-testid="aircall-phones-input"
                />
                <p className="text-xs text-muted-foreground">Separes par des virgules</p>
              </div>

              {/* Sync options */}
              <div className="flex items-center justify-between">
                <Label>Synchroniser les appels</Label>
                <Switch
                  checked={connectForm.sync_calls}
                  onCheckedChange={v => setConnectForm({ ...connectForm, sync_calls: v })}
                  data-testid="aircall-sync-calls-switch"
                />
              </div>

              <div className="space-y-2">
                <Label>Historique a synchroniser (jours)</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={connectForm.sync_from_days}
                  onChange={e => setConnectForm({ ...connectForm, sync_from_days: parseInt(e.target.value) || 30 })}
                  data-testid="aircall-sync-days-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsConnectOpen(false)}>Annuler</Button>
              <Button onClick={handleConnect} disabled={!connectForm.aircall_user_id} data-testid="confirm-connect-aircall-btn">
                Connecter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
